<template>
  <div
    id="playoff-table"
    ref="tableviewWrapper"
    class="position-relative flex-grow-1 d-flex flex-column h-100 pb-4"
  >
    <article
      class="px-0 d-flex position-relative tournament-playoff-wrapper flex-grow-1 flex-column"
    >
      <HorizontalTabs
        v-if="roundTabs.length"
        ref="roundTabs"
        v-bind="{
          tabs:
            currentTournamentBlock.type === 'double_elimination'
              ? doubleRound
              : roundTabs,
          notExactMatch: true,
        }"
        variant="folders"
        class="custom-scrollbar sticky-top tournament-playoff-wrapper__tabs"
      />

      <Panel
        class="tournament-playoff-main flex-grow-1 px-0 "
        :class="{
          'ph-panel--rounded-top-left-0 ph-panel--rounded-top-right-0 pt-0':
            roundTabs.length,
        }"
      >
        <!-- filters -->
        <section
          ref="tableFilters"
          class="tournament-playoff-filter px-4 pb-3 sticky-top"
          :class="{
            'pt-5': roundTabs.length,
            'pt-2': !roundTabs.length,
          }"
        >
          <div class="d-flex justify-content-between align-items-center">
            <SearchInput
              v-model="searchText"
              :debounce="debounce"
              :placeholder="
                $t('_web_battles-list-search-placeholder', 'Search battle')
              "
              class="col-3 px-0 h-mr-3 tableView__searchInput"
            />
            <RoundChatButton
              :block-id="competitionID"
              :round="currentRoundIndex"
            />

            <div class="d-flex align-items-center">
              <template v-for="(item, index) in getRoundTools()">
                <ToolIcon
                  :key="item.key"
                  :help-text="item.helpText"
                  :icon-name="item.iconName"
                  :disabled="item.disabled"
                  variant="circle"
                  class="h-ml-3"
                  @click.stop="item.action"
                />
                <template v-if="!index && !$lodash.isEmpty(currentRound)">
                  <TextBlock
                    :key="`round-date-label-${index}`"
                    :size="0.75"
                    :variant="'secondary'"
                    class="h-ml-3"
                    :line-height="1"
                    >{{
                      currentRound.date_start | localDate($times.DATE_SUBMED)
                    }},</TextBlock
                  >
                  <TextBlock
                    :key="`round-time-label-${index}`"
                    class="h-ml-3 h-mr-6"
                    :size="0.75"
                    :variant="'secondary'"
                    :line-height="1"
                    >{{
                      currentRound.date_start | localDate($times.TIME_SIMPLE)
                    }}</TextBlock
                  >
                </template>
              </template>
            </div>
          </div>
        </section>
        <!-- table content -->
        <div
          class="tournament-playoff-content p-0 pt-3 mt-0 position-relative h-100 "
          :class="{ 'pre-loader': isLoading }"
        >
          <div
            class="d-flex sticky-top align-items-start justify-content-between tableView-head"
          >
            <div class="px-4 pt-3 pb-1 match-index">
              <TextLabel :text="$t('_web_match-th-label', 'Match')" />
            </div>
            <div class="h-mr-auto pt-3 pb-1 participants-column">
              <TextLabel :text="$t('_web_battle_name-th-label', 'Name')" />
            </div>
            <div
              class="h-ml-5 d-flex results-column-wrapper"
              :style="{ flexBasis: `${46 * matchRounds.length}px` }"
            >
              <div
                v-for="(round, index) in matchRounds"
                :key="index + '-header'"
                class="pt-3 pb-1 results-column"
              >
                <div class="text-center">
                  <TextLabel
                    :text="
                      $t('_web_battle_match-game-th-label', 'Game {index}', {
                        index: index + 1,
                      })
                    "
                  />
                </div>
              </div>
            </div>
            <div class="text-center pt-3 pb-1 mx-5 summary-column">
              <TextLabel
                :text="$t('_web_battle_summary-th-label', 'Summary')"
              />
            </div>
            <div class="pt-3 pb-1 h-mr-5 status-column">
              <TextLabel
                :text="$t('_web_battle_status-th-label', 'Battle Status')"
              />
              <CustomSelector
                v-model="selectedStatuses"
                :options="statusesOptions"
                not-selected-by-default
                clear-if-selected-all
                label-key="statusFilter"
                :disabled="!filteredMatches.length"
                custom-height="280px"
                dark
                custom-order
                class="my-3"
              />
            </div>
            <div
              class="h-ml-auto h-pr-4 h-pl-4 pt-3 pb-1 text-left actions-column"
            >
              <TextLabel :text="$t('_web_battle_action-th-label', 'Action')" />
            </div>
          </div>
          <VirtualScroll
            v-if="filteredMatches.length"
            ref="tableRows"
            v-bind="{
              cacheCount: 4,
              item: getItem,
              itemHeight: 124,
              visibleCount: 10,
              itemsCount: filteredMatches.length,
              itemProps: getMatchProps,
              startIndex: startScrollIndex,
            }"
          />
          <div v-if="isLoading" class="bg-transparent">
            <HeraDummyList :size="200" />
          </div>
          <div v-else-if="!filteredMatches.length" class="bg-transparent">
            <HeraDummyList
              icon-name="empty-competitions"
              :header-text="
                $t(
                  '_web_stage_matches_empty',
                  'Match list is empty at the moment'
                )
              "
              :size="200"
            >
              <div slot="ctaButton">
                <Btn
                  class="btn-accent-simple mb-4"
                  :label="$t('_web_tournament_go-to-stage-edit', 'Setup stage')"
                  @click.stop="goToBracketsSettings"
                />
              </div>
            </HeraDummyList>
          </div>
        </div>
      </Panel>
    </article>
  </div>
</template>

<script>
//utils
import { mapActions, mapGetters, mapMutations } from 'vuex';
import { getPlayerScore } from '@/views/tournaments/utils/players';
//constants
import { BATTLE_STATUSES } from '@/constants/battles';
//mixins
import matchHelper from '@/views/tournaments/management/brackets/mixins/matchHelper';
import roundHelper from '@/views/tournaments/management/brackets/mixins/roundHelper';
import { matchStatuses } from '@/mixins/tournaments/matchStatuses';
import matchMenu from '@/views/tournaments/management/brackets/mixins/matchMenu';
import loadingHelper from '@/mixins/isLoading';
//components
import TextBlock from '@/components/atoms/TextBlock';
import CustomSelector from '@/components/form/MultiSelectBox';
import SearchInput from '@/components/molecules/form/HeraSearchInput';
import HeraDummyList from '@/components/common/HeraDummyList';
import HorizontalTabs from '@/components/common/HorizontalTabs';
import Panel from '@/components/atoms/Panel';
import ToolIcon from '@/views/tournaments/management/brackets/components/ToolIcon';
import TextLabel from '@/components/atoms/TextLabel';
import TableViewRowItem from './TableViewRowItem';
import VirtualScroll from '@/components/common/VirtualScroll';
import Btn from '@/components/atoms/HeraFormBtn';
import RoundChatButton from './RoundChatAction.vue';
import { prop } from '@/utils/factories';
import {
  STAGE_STATUSES,
  COMPETITION_STATUSES,
  CONSTRUCTOR_MODES,
} from '@/constants/tournaments';

const DEFAULT_FILTERS = () => ({
  status: [],
  search: null,
  providedResults: null,
});
const delayTime = 200;

export default {
  name: 'TournamentPlayoffTable',
  components: {
    SearchInput,
    TextBlock,
    HeraDummyList,
    HorizontalTabs,
    Panel,
    TextLabel,
    ToolIcon,
    VirtualScroll,
    CustomSelector,
    Btn,
    RoundChatButton,
  },
  mixins: [loadingHelper, matchHelper, roundHelper, matchMenu, matchStatuses],
  props: {
    roundID: {
      type: [Number, String],
      default: 0,
    },
    roundName: prop(Function),
    grids: prop(Array, null),
    prepareMenuItems: prop(Object, null),
    sortRounds: prop(Function, null),
  },
  data() {
    return {
      getPlayerScore,
      message: '',
      BATTLE_STATUSES,
      isResultsLoading: false,
      startScrollIndex: 0,
      debounce: 500,
      searchText: '',
      rounds: [],
      roundsIsLoaded: false,
      matchRounds: 1,
      matches: [],
      filter: DEFAULT_FILTERS(),
      pageSize: 20,
      captainsByRound: [],
      isSaveDisabled: false,
      //dots menu
      optionsPosition: null,
      optionsData: null,
      //Results match modal vars
      showResultsModal: false,
      editResultsMode: false,
      editResultsData: {},
      //edit battle vars
      currentEditableItem: '',
      menuItems: [],
      selectedStatuses: [],
      statuses: {
        chosen: null,
        options: [
          BATTLE_STATUSES.pending,
          BATTLE_STATUSES.scheduled,
          BATTLE_STATUSES.started,
          BATTLE_STATUSES.judging,
          BATTLE_STATUSES.completed,
          BATTLE_STATUSES.cancelled,
        ],
      },
      providedResults: {
        chosen: null,
        options: [
          {
            text: this.$t(
              '_web_tournaments-tableview_dropdown-empty-option',
              'all'
            ),
            value: 'all',
          },
          {
            text: this.$t(
              '_web_tournaments-tableview_dropdown-provided-option',
              'Provide'
            ),
            value: 'provide',
          },
          {
            text: this.$t(
              '_web_tournaments-tableview_dropdown-no-results-option',
              'No results'
            ),
            value: 'no_result',
          },
        ],
      },
    };
  },
  computed: {
    ...mapGetters([
      'currentTournament',
      'isScheduled',
      'isStarted',
      'isSingle',
      'hasGroupStage',
      'currentTournamentBlock',
      'currentTournamentStage',
      'isCompetitionFieldDisabled',
      'isStageFieldDisabled',
      'isTournamentFieldDisabled',
    ]),
    isRoundFinished() {
      return !this.matches.some(match => {
        return match.status !== 'cancelled' && match.status !== 'completed';
      });
    },

    isRoundActive() {
      return this.matches.some(
        match => match.player_1 !== null && match.player_2 !== null
      );
    },

    isRoundFull() {
      return this.matches.filter(
        match =>
          match.player_1 !== null ||
          (match.player_2 !== null &&
            match.status === ('completed' || 'canceled'))
      ).length
        ? false
        : true;
    },
    filteredMatches() {
      return (this.searchText && this.searchText.length > 1) ||
        this.selectedStatuses.length
        ? this.matches.filter(match => {
            const seedByStatus =
              !this.selectedStatuses.length ||
              this.selectedStatuses.includes(match.status);
            let seedBySearch;

            if (!this.searchText || this.searchText.length < 2) {
              seedBySearch = true;
            } else {
              const searchString = `${match.battle_id}${match.index + 1}${
                match.player_1 ? match.player_1.name : ''
              }${match.player_2 ? match.player_2.name : ''}${match.status}${
                match.captain_1
              }${match.captain_2}`.toLowerCase();
              seedBySearch =
                searchString.indexOf(this.searchText.toLowerCase()) > -1;
            }
            return seedByStatus && seedBySearch;
          })
        : this.matches;
    },
    currentRoundIndex() {
      return this.roundID;
    },
    currentRound() {
      return (
        (this.rounds || []).find(el =>
          this.currentGrid
            ? el.index === +this.currentRoundIndex &&
              el.grid === this.currentGrid
            : el.index === +this.currentRoundIndex
        ) || {}
      );
    },
    currentGrid() {
      return this.$lodash.get(this.$route, 'params.grid', null);
    },
    isLowerGrid() {
      return this.currentGrid === 'lower';
    },
    stageID() {
      return this.$route.params.stageID;
    },
    tournamentID() {
      return this.$route.params.tournamentID;
    },
    competitionID() {
      return this.$route.params.blockID === 0
        ? this.$lodash.get(
            this.currentTournamentStage,
            `tournamentBlocks[${this.$route.params.blockID}].id`
          )
        : this.$route.params.blockID;
    },
    roundTabs() {
      // const query = this.$lodash.cloneDeep(this.$route.query);
      // delete query.scrollOffset;
      // delete query.scrollTo;
      return this.rounds.map(round => ({
        id: round.grid ? `${round.grid}-${round.index}` : round.index,
        route: {
          name: this.$route.name,
          params: {
            ...this.$route.params,
            roundID: round.index,
            ...(round.grid ? { grid: round.grid } : null),
          },
        },
        labelStated: this.roundName(this.rounds, round.index, round),
      }));
    },
    doubleRound() {
      const sortedRounds = this.rounds
        .slice()
        .sort((a, b) => a.date_start - b.date_start);

      return sortedRounds.map(round => ({
        id: round.grid ? `${round.grid}-${round.index}` : round.index,
        route: {
          name: this.$route.name,
          params: {
            ...this.$route.params,
            roundID: round.index,
            ...(round.grid ? { grid: round.grid } : null),
          },
        },
        labelStated: this.roundName(this.rounds, round.index, round),
      }));
    },

    canFinishAll() {
      return (
        this.currentTournamentBlock &&
        this.currentTournamentBlock.status === COMPETITION_STATUSES.STARTED &&
        this.currentTournamentStage &&
        this.currentTournamentStage.status === STAGE_STATUSES.STARTED &&
        this.matches.length
      );
    },
    canStartAll() {
      return !(
        (this.currentTournamentBlock &&
          this.currentTournamentBlock.status === COMPETITION_STATUSES.DRAFT) ||
        this.isCompetitionFieldDisabled(
          'round_start',
          this.currentTournamentBlock
        ) ||
        (this.currentTournamentStage &&
          this.currentTournamentStage.status === STAGE_STATUSES.DRAFT) ||
        this.isStageFieldDisabled('round_start', this.currentTournamentStage)
      );
    },
    canChangeRoundDate() {
      return (
        !this.isTournamentFieldDisabled('round_date') &&
        !this.isStageFieldDisabled('round_date') &&
        !this.isCompetitionFieldDisabled(
          'round_date',
          this.currentTournamentBlock
        )
      );
    },
    statusesOptions() {
      return this.statuses.options.map(status => ({
        id: status,
        name: `${this.$filters.startCase(
          this.localizedMatchStatuses[status]
        )} (${this.statusesCounters[status] || 0})`,
      }));
    },
    statusesCounters() {
      return this.matches.reduce((counters, match) => {
        const current = counters[match.status] || 0;
        counters[match.status] = current + 1;
        return counters;
      }, {});
    },
  },
  watch: {
    $route() {
      this.clearStateData();
      this.getMatches();
    },
    roundTabs(newRounds) {
      if (Array.isArray(newRounds) && newRounds.length) {
        this.updateTopOffset(this.gridHeadTopOffset);
      }
    },
  },
  created() {
    this.$options.roundTools = [];
    if (this.tournamentID) {
      this.init();
    }
  },
  mounted() {
    // this.sendtoRound();
    const $navbar = document.getElementById('nav');
    const $actionButtons = document.getElementById('breadcrumbsWrapper');
    let gridHeadTopOffset = 0;
    if ($navbar) {
      gridHeadTopOffset += Math.floor($navbar.clientHeight);
    }
    if ($actionButtons) {
      gridHeadTopOffset += Math.floor($actionButtons.clientHeight);
    }
    this.gridHeadTopOffset = gridHeadTopOffset;
    this.updateTopOffset(gridHeadTopOffset);
  },
  methods: {
    ...mapMutations([
      'setActivePopoverChat',
      'showPopoverChat',
      'openConversationInSliderChatEditor',
      'clearTempMatchResults',
      'setCurrentTournamentStage',
      'updateModalProps',
    ]),
    ...mapActions([
      'getCompetitionRounds',
      'getCompetitionMatches',
      'sendMessagetoRound',
      'getBattleResultsGroup',
      'getBattleResults',
      'errorNotify',
      //chats
      'getConversationInfo',
      'getGroupConversationInfo',
      'updateConversation',
      'createTournamentConversationBySignature',
      'openModal',
      'startTournamentChat',
    ]),
    goToBracketsSettings() {
      this.$router.push({
        name: 'multiStage',
        params: {
          ...this.$route.params,
          constructorMode: CONSTRUCTOR_MODES.CONSTRUCT,
        },
      });
    },
    getItem() {
      return TableViewRowItem;
    },
    getMatchProps(matchIndex) {
      const match = this.filteredMatches[matchIndex];
      return {
        key: `${match.battle_id || matchIndex}-${match.status}`,
        props: {
          match,
          numberOfGames: this.matchRounds.length,
          roundName: this.roundName(
            this.rounds,
            this.currentRound.index,
            this.currentRound
          ),
          prevRoundEndDate: this.rounds[
            Math.max((+this.currentRoundIndex || 0) - 1, 0)
          ].date_end,
        },
        class: {
          striped: matchIndex % 2 === 0,
        },
        on: {
          matchStarted: () =>
            this.$lodash.delay(this.getMatches, delayTime, {
              from: match.index,
              to: match.index,
              forceUpdate: true,
            }),
          matchFinished: () =>
            this.$lodash.delay(this.getMatches, delayTime, {
              from: match.index,
              to: match.index,
              forceUpdate: true,
            }),
          matchReseted: () =>
            this.$lodash.delay(this.getMatches, delayTime, {
              from: match.index,
              to: match.index,
              forceUpdate: true,
            }),
          matchUpdated: this.updateMatchByIndex,
        },
      };
    },
    getRoundTools() {
      return [
        {
          helpText: this.$t(
            '_web_battles_schedule-round-help',
            'Change start date and time of the round'
          ),
          iconName: 'calendar',
          disabled: !this.matches.length,
          action: this.openRoundSchedulingModal,
        },
        {
          helpText: this.$t(
            '_web_battles_send-to-round',
            'Send message to round'
          ),
          iconName: 'chat-group',
          disabled: !this.isRoundActive || this.isRoundFinished,
          action: this.confirmSendRound,
        },
        {
          helpText: this.$t(
            '_web_battles_start-all-help',
            'Start all matches in this round'
          ),
          iconName: 'play-2',
          disabled: !this.canStartAll,
          action: this.confirmStartRound,
        },
        {
          helpText: this.$t(
            '_web_battles_finish-all-help',
            'Finish all matches in this round'
          ),
          iconName: 'stop',
          // disabled: this.canFinishAll,
          action: this.confirmFinishRound,
        },
      ];
    },
    getMatchTools(match) {
      return [
        {
          helpText: this.$t(
            '_web_battles_schedule-match-help',
            'Change schedule of the match'
          ),
          iconName: 'calendar',
          disabled:
            !this.canChangeMatchDate(match.status) &&
            !this.canChangeMatchDuration(match.status),
          action: () => this.openMatchSchedulingModal(match.index),
        },
        {
          helpText: this.$t('_web_battles-group-chat', 'Group chat'),
          iconName: 'chat-group',
          disabled: false,
          action: () => this.openMatchChat(match),
        },
        {
          helpText: this.$t(
            '_web_tournament_match-menu-item-edit-results',
            'Edit results'
          ),
          iconName: 'scales',
          disabled: false,
          action: () => this.openMatchResultsEdit(match),
        },
        {
          helpText: this.$t('_web_battles-reset-match', 'Reset match'),
          iconName: 'reset',
          disabled: !this.canRestartMatch(match),
          action: () => this.confirmResetMatch(match),
        },
        {
          helpText: this.$t('_web_battles-start-match', 'Start match'),
          iconName: 'play-2',
          disabled: !this.canStartMatch(match),
          action: () => this.confirmStartMatch(match),
        },
        {
          helpText: this.$t('_web_battles-finish-match', 'Finish match'),
          iconName: 'stop',
          disabled: !this.canFinishMatch(match),
          action: () => this.confirmFinishMatch(match),
        },
      ];
    },
    updateTopOffset(startOffset = 0) {
      this.$nextTick(() => {
        const tabsHeight = this.$refs.roundTabs
          ? Math.floor(+this.$refs.roundTabs.$el.clientHeight || 48)
          : 0;
        const filtersHeight = this.$refs.tableFilters
          ? Math.floor(+this.$refs.tableFilters.clientHeight || 0)
          : 0;
        this.$refs.tableviewWrapper.style.setProperty(
          '--tabs-top',
          `${startOffset}px`
        );
        this.$refs.tableviewWrapper.style.setProperty(
          '--filters-top',
          `${startOffset + tabsHeight}px`
        );
        this.$refs.tableviewWrapper.style.setProperty(
          '--table-top',
          `${startOffset + tabsHeight + filtersHeight}px`
        );
      });
    },
    getSubMenuItems(roundsInput, grid = null) {
      let rounds = roundsInput.map(round => ({
        ...this.$lodash.cloneDeep(round),
        ...(grid && { grid }),
        date_start: round.date_start,
        key: `round_${round.index + 1}${grid ? '_' + grid : ''}`,
      }));
      if (this.prepareMenuItems) {
        Object.entries(this.prepareMenuItems).forEach(([proto, callback]) => {
          rounds = rounds[proto](callback, {}) || rounds;
        });
      }
      return rounds;
    },
    clearStateData() {
      this.matches = [];
      this.startScrollIndex = 0;
      this.filter = DEFAULT_FILTERS();
    },
    init() {
      this.setLoading();
      let tempRounds;
      if (this.stageID && this.currentTournamentStage.id !== this.stageID) {
        this.setCurrentTournamentStage(this.stageID);
      }
      (this.grids && this.grids.length
        ? Promise.all(this.grids.map(this.fetchRoundsData)).then(rounds => {
            tempRounds = rounds.reduce((acc, el, index) => {
              acc = [...acc, ...this.getSubMenuItems(el, this.grids[index])];
              return acc;
            }, []);
          })
        : this.fetchRoundsData().then(rounds => {
            tempRounds = [...rounds];
          })
      )
        .then(() => {
          this.rounds = this.sortRounds
            ? this.sortRounds(tempRounds)
            : tempRounds;
        }) // now it is fo upper
        .then(() => {
          if (this.rounds.length === 0 && !this.isLowerGrid) {
            this.$emit('noDataToShow');
          } else if (this.rounds.length > 0 && !this.isLowerGrid) {
            this.$emit('notEmpty');
          }
          this.roundsIsLoaded = true;
        })
        .then(() => this.getMatches())
        .catch(this.errorNotify)
        .finally(this.unsetLoading);
    },
    onFilterChange() {
      this.$nextTick(() => {
        this.matches = [];
        this.getMatches({ forceUpdate: true });
      });
    },
    name(player) {
      return player ? this.$filters.displayName(player) : '';
    },
    logo(player) {
      return player ? player.logo || player.avatar : false;
    },
    fetchRoundsData(grid = null) {
      return this.getCompetitionRounds({
        grid,
        competitionID: this.competitionID,
      });
    },
    openRoundSchedulingModal() {
      this.openModal({
        name: 'RoundSchedulingModal',
        props: {
          round: this.currentRound,
          minDate: this.minRoundDatetime(),
          maxDate: this.maxRoundDatetime(),
          canChangeRoundDate: this.canChangeRoundDate,
        },
        events: {
          save: this.onRoundDateChanged,
        },
        settings: {
          clickOnBackdrop: false,
        },
      });
    },
    onRoundDateChanged() {
      this.matches = [];
      this.$nextTick(() => {
        this.init();
      });
    },
    openChat({
      conversationType,
      targetGroup,
      roundIndex,
      id,
      index: matchIndex,
    }) {
      const options = {
        tournamentId: this.tournamentID,
        round: roundIndex,
        conversationType,
        targetGroup,
        captainsOnly: true,
        visible: false,
        blockId: this.currentTournamentBlock.id,
      };
      if (targetGroup === 'all' && id) {
        options.matchId = id;
      }
      this.createTournamentConversationBySignature(options)
        .then(conversation =>
          this.openConversationInSliderChatEditor({
            conversation,
            predefinedTitle:
              options.matchId &&
              this.$t('_web_match_chat_title', `Match Id: {match} `, {
                match: matchIndex + 1,
              }),
            predefinedSubtitle: '',
          })
        )
        .catch(this.errorNotify);
    },
    //all matches  by round
    sendtoRound() {
      if (this.message === '') {
        return;
      } else {
        this.sendMessagetoRound({
          block: this.currentTournamentBlock.id,
          tournamentID: this.currentTournament.id,
          roundID: this.currentRoundIndex,
          message: this.message,
          CompleteRound: this.isRoundFull,
        })
          .then(this.closeModal())
          .catch(e =>
            this.errorNotify({
              message: e.message,
              inModal: true,
              yesText: this.$t('_web_form_error_confirm-yes', 'OK'),
            })
          );
      }
    },

    getMatches({
      round = this.currentRoundIndex,
      grid = this.currentGrid,
      from = 0,
      to = this.currentRound.matches,
      forceUpdate = false,
    } = {}) {
      if (this.$lodash.isEmpty(this.currentRound)) {
        return;
      }
      if (from === to) {
        this.isResultsLoading = this.matches[from].battle_id;
      } else {
        this.setLoading();
      }
      return this.getCompetitionMatches({
        competitionID: this.competitionID,
        queryParams: {
          round,
          from,
          to,
          ...(grid ? { grid } : null),
        },
      })
        .then(matches => {
          if (to - from === this.currentRound.matches) {
            this.matches = matches;
          } else {
            this.updateMatchByIndex(matches[0]);
          }
        })
        .then(() => {
          if (!this.matches.length) {
            return;
          }
          this.matchRounds = Array(this.matches[0].number_of_rounds);
        })
        .catch(this.errorNotify)
        .finally(() => {
          if (forceUpdate || this.$route.query.scrollTo) {
            if (this.$route.query.scrollTo) {
              this.startScrollIndex = this.filteredMatches.findIndex(
                el => el.id === this.$route.query.scrollTo
              );
              window.scrollTo(0, this.$route.query.scrollOffset);
            }
          }
          this.unsetLoading();
        });
    },
    // update match by index in table
    updateMatchByIndex(match) {
      if (match && match.id === this.matches[match.index].id) {
        this.$set(this.matches, match.index, match);
      }
    },
    /** Chat open */
    openPopoverChat(event, login) {
      if (!event.ctrlKey) {
        this.getConversationInfo({ conversationId: login })
          .then(res => {
            this.setActivePopoverChat(res.id);
            this.showPopoverChat();
          })
          .catch(this.errorNotify);
      }
    },
    onRoundStart() {
      this.startRound({
        roundID: this.currentRoundIndex,
        ...(this.currentGrid ? { grid: this.currentGrid } : null),
      }).then(() => {
        this.$lodash.delay(this.onFilterChange, delayTime);
      });
    },
    onRoundFinish() {
      this.finishRound({
        roundID: this.currentRoundIndex,
        ...(this.currentGrid ? { grid: this.currentGrid } : null),
      }).then(() => {
        this.$lodash.delay(this.onFilterChange, delayTime);
      });
    },
    /**
     * Round confirm functions
     */
    confirmStartRound() {
      this.openModal({
        name: 'HeraConfirm',
        props: {
          text: this.$t(
            '_web_tournaments-tableview_start-round-message',
            'Now all battles in scheduled status will be started. Do you really want to proceed?'
          ),
        },
        events: {
          cancel: this.closeModal,
          confirm: this.onRoundStart.bind(null),
        },
      });
    },
    confirmSendRound() {
      this.openModal({
        name: 'SendRoundMessageModal',
        props: {
          text: this.$t(
            '_web_tournaments-tableview_send-round-message',
            'send message to all round Participants'
          ),
          setMessage: message => {
            this.message = message;
          },
          cancelButtonText: 'CANCEL',
          confirmButtonText: 'SEND',
        },
        settings: {
          variant: 'fit-content',
        },
        events: {
          cancel: this.closeModal,
          confirm: this.sendtoRound.bind(this),
        },
      });
    },
    confirmFinishRound() {
      this.openModal({
        name: 'HeraConfirm',
        props: {
          text: this.$t(
            '_web_tournaments-tableview_finish-round-message',
            'Now all NOT COMPLETED matches will be closed. Matches  without results will be cancelled, for matches with results the system will define a winner'
          ),
        },
        events: {
          cancel: this.closeModal,
          confirm: this.onRoundFinish.bind(null),
        },
      });
    },
  },
};
</script>
<style lang="scss">
#playoff-table {
  .horizontal-tabs-wrapper {
    height: 48px;
    overflow-y: hidden;
    .horizontal-slider,
    .horizontal-tabs__item {
      height: 48px;
      ul {
        height: 100%;
      }
    }
  }
  .results-horizontal {
    .round-box {
      max-width: 44px;
    }
  }
}
</style>
<style scoped lang="scss">
#playoff-table {
  --tabs-top: 0;
  --filters-top: 0;
  --table-top: 0;
  .horizontal-tabs-wrapper {
    top: var(--tabs-top);
    background: $ph-bg;
    .horizontal-slider,
    .horizontal-tabs__item {
      height: 48px;
    }
  }
  .tournament-playoff-filter {
    top: var(--filters-top);
    background: $ph-card;
    z-index: 1021;
  }
  .tableView__searchInput {
    margin-bottom: 0px !important;
  }
  .tableView-head {
    flex-basis: fit-content;
    top: var(--table-top);
    background: $ph-card;
    & > div {
      &.participants-column {
        flex-basis: 184px;
      }
      &.results-column-wrapper .results-column {
        flex-basis: 46px;
      }
      &.status-column {
        flex-basis: 130px;
        max-width: 130px;
      }
      &.summary-column {
        flex-basis: 85px;
      }
      &.actions-column {
        flex-basis: 312px;
      }
    }
  }
}
.tournament {
  &-playoff {
    &-main {
      .status-filter-wrapper {
        .status-filter-badge {
          &.disabled {
            opacity: 0.5;
            pointer-events: none;
          }
        }
      }
      .row-options {
        cursor: pointer;
        opacity: 0.5;
        transition: opacity 0.1s;
        width: 1.125rem;
        &-wrapper {
          width: 1.125rem;
        }
        &:hover {
          opacity: 1;
        }
      }
    }
  }
}
</style>
